import { motion } from 'framer-motion';

// ── Individual screen mockups ─────────────────────────────────────────────────

function DashboardScreen() {
  return (
    <div className="px-3 pt-2 space-y-2 overflow-hidden">
      {/* Date + greeting */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[7px] text-white/30">Monday · Feb 23</p>
          <p className="text-[10px] font-semibold text-white">Good morning ☀️</p>
        </div>
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold text-teal-300"
          style={{ background: 'rgba(79,137,132,0.18)', border: '1px solid rgba(79,137,132,0.3)' }}
        >
          MG
        </div>
      </div>

      {/* Sobriety card */}
      <div
        className="rounded-2xl p-2.5"
        style={{
          background: 'linear-gradient(135deg, rgba(79,137,132,0.16), rgba(47,57,87,0.25))',
          border: '1px solid rgba(79,137,132,0.22)',
        }}
      >
        <p className="text-[6px] text-teal-400/70 font-semibold uppercase tracking-widest mb-0.5">
          Alcoholics Anonymous
        </p>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-white leading-none">247</span>
          <span className="text-[9px] text-teal-400 font-medium">days</span>
        </div>
        <p className="text-[6px] text-white/25 mt-0.5">8 months · 1 week · 2 days</p>
        <div className="mt-1.5 flex gap-0.5">
          {[7, 30, 60, 90, 180, 247].map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full"
              style={{ background: i < 5 ? '#4f8984' : 'rgba(79,137,132,0.25)' }}
            />
          ))}
        </div>
      </div>

      {/* Check-in grid */}
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { emoji: '☀️', label: 'Morning', done: true },
          { emoji: '🌙', label: 'Nightly', done: false },
        ].map((item) => (
          <div
            key={item.label}
            className="rounded-xl p-2"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${item.done ? 'rgba(79,137,132,0.3)' : 'rgba(255,255,255,0.07)'}`,
            }}
          >
            <span className="text-xs">{item.emoji}</span>
            <p className="text-[8px] font-semibold text-white mt-0.5">{item.label}</p>
            <p
              className="text-[6.5px] font-medium"
              style={{ color: item.done ? '#4abbb5' : 'rgba(255,255,255,0.28)' }}
            >
              {item.done ? '✓ Complete' : 'Tap to check in'}
            </p>
          </div>
        ))}
      </div>

      {/* Step progress */}
      <div
        className="rounded-xl p-2"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex justify-between items-center mb-1">
          <p className="text-[8px] font-semibold text-white">Step Work</p>
          <span
            className="text-[6px] font-semibold px-1.5 py-0.5 rounded-full"
            style={{ background: 'rgba(79,137,132,0.18)', color: 'rgb(100,200,195)' }}
          >
            Step 5 →
          </span>
        </div>
        <div className="flex gap-0.5">
          {Array.from({ length: 12 }, (_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full"
              style={{
                background:
                  i < 4 ? '#4f8984' : i === 4 ? 'rgba(79,137,132,0.38)' : 'rgba(255,255,255,0.07)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MorningCheckinScreen() {
  return (
    <div className="px-3 pt-2 space-y-2 overflow-hidden">
      {/* Header */}
      <div>
        <p className="text-[6px] text-teal-400/70 font-semibold uppercase tracking-widest">
          Morning Check-In
        </p>
        <p className="text-[10px] font-semibold text-white">How are you feeling?</p>
      </div>

      {/* Mood row */}
      <div className="flex justify-between">
        {['😔', '😕', '😐', '🙂', '😊'].map((emoji, i) => (
          <div
            key={i}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-base"
            style={{
              background: i === 3 ? 'rgba(79,137,132,0.3)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${i === 3 ? 'rgba(79,137,132,0.6)' : 'rgba(255,255,255,0.07)'}`,
            }}
          >
            {emoji}
          </div>
        ))}
      </div>

      {/* Gratitude */}
      <div
        className="rounded-xl p-2.5"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <p className="text-[7px] text-white/40 mb-1 font-medium">Today I'm grateful for…</p>
        <p className="text-[8px] text-white/70 leading-relaxed">
          My sponsor, coffee, and another day without a drink.
        </p>
        <div className="mt-1.5 h-px bg-white/5 w-2/3" />
        <div className="mt-1 h-px bg-white/5 w-1/2" />
      </div>

      {/* Intention */}
      <div
        className="rounded-xl p-2.5"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <p className="text-[7px] text-white/40 mb-1 font-medium">My intention for today…</p>
        <p className="text-[8px] text-white/70 leading-relaxed">
          Stay in the moment. Call my sponsor.
        </p>
      </div>

      {/* Submit */}
      <div
        className="rounded-xl py-2 text-center"
        style={{ background: '#4f8984' }}
      >
        <p className="text-[8px] font-semibold text-white">Save Check-In ✓</p>
      </div>
    </div>
  );
}

function NightlyReviewScreen() {
  return (
    <div className="px-3 pt-2 space-y-2 overflow-hidden">
      {/* Header */}
      <div>
        <p className="text-[6px] text-teal-400/70 font-semibold uppercase tracking-widest">
          Nightly Review
        </p>
        <p className="text-[10px] font-semibold text-white">How did today go?</p>
      </div>

      {/* Day rating */}
      <div className="flex gap-1">
        {['Rough', 'Okay', 'Good', 'Great'].map((label, i) => (
          <div
            key={label}
            className="flex-1 py-1.5 rounded-xl text-center"
            style={{
              background: i === 2 ? 'rgba(79,137,132,0.3)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${i === 2 ? 'rgba(79,137,132,0.5)' : 'rgba(255,255,255,0.07)'}`,
            }}
          >
            <p
              className="text-[6.5px] font-semibold"
              style={{ color: i === 2 ? '#4abbb5' : 'rgba(255,255,255,0.4)' }}
            >
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* Reflection prompts */}
      <div
        className="rounded-xl p-2.5"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <p className="text-[7px] text-white/40 mb-1 font-medium">Where did I fall short?</p>
        <p className="text-[8px] text-white/70 leading-relaxed">
          Got frustrated in traffic. Reacted without pausing first.
        </p>
      </div>

      <div
        className="rounded-xl p-2.5"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <p className="text-[7px] text-white/40 mb-1 font-medium">What am I thankful for?</p>
        <p className="text-[8px] text-white/70 leading-relaxed">
          Got through the day clean. Meeting at 7pm helped.
        </p>
      </div>

      {/* AI badge */}
      <div
        className="rounded-xl p-2 flex items-center gap-2"
        style={{
          background: 'linear-gradient(135deg, rgba(79,137,132,0.12), rgba(47,57,87,0.2))',
          border: '1px solid rgba(79,137,132,0.25)',
        }}
      >
        <span className="text-sm">✨</span>
        <div>
          <p className="text-[7px] font-semibold text-teal-300">AI Analysis</p>
          <p className="text-[6px] text-white/40">Insights from your review (Premium)</p>
        </div>
      </div>
    </div>
  );
}

function StepWorkScreen() {
  return (
    <div className="px-3 pt-2 space-y-2 overflow-hidden">
      {/* Header */}
      <div>
        <p className="text-[6px] text-teal-400/70 font-semibold uppercase tracking-widest">
          Step Work · AA
        </p>
        <p className="text-[10px] font-semibold text-white">Step 4 Inventory</p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between mb-0.5">
          <p className="text-[6.5px] text-white/30">Reflection Progress</p>
          <p className="text-[6.5px] text-teal-400">68%</p>
        </div>
        <div className="h-1 w-full rounded-full" style={{ background: 'rgba(255,255,255,0.07)' }}>
          <div className="h-1 rounded-full w-2/3" style={{ background: '#4f8984' }} />
        </div>
      </div>

      {/* Reflection prompt */}
      {[
        { q: 'Who or what made you resentful?', a: 'My employer for passing me over.', done: true },
        { q: 'What did they affect in your life?', a: 'My self-esteem and finances.', done: true },
        { q: 'What was your part?', a: '', done: false },
      ].map((item) => (
        <div
          key={item.q}
          className="rounded-xl p-2"
          style={{
            background: item.done ? 'rgba(79,137,132,0.08)' : 'rgba(255,255,255,0.035)',
            border: `1px solid ${item.done ? 'rgba(79,137,132,0.22)' : 'rgba(255,255,255,0.07)'}`,
          }}
        >
          <div className="flex items-start justify-between gap-1 mb-0.5">
            <p className="text-[6.5px] text-white/40 flex-1">{item.q}</p>
            {item.done && <span className="text-[8px] text-teal-400 flex-shrink-0">✓</span>}
          </div>
          {item.a ? (
            <p className="text-[7.5px] text-white/65 leading-relaxed">{item.a}</p>
          ) : (
            <div className="h-1 w-4/5 rounded bg-white/10 mt-1" />
          )}
        </div>
      ))}

      {/* Submit bar */}
      <div
        className="rounded-xl py-1.5 flex items-center justify-center gap-1"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <p className="text-[7px] text-white/30">Complete all prompts to submit</p>
      </div>
    </div>
  );
}

function SponsorScreen() {
  return (
    <div className="px-3 pt-2 space-y-2 overflow-hidden">
      {/* Header */}
      <div>
        <p className="text-[6px] text-teal-400/70 font-semibold uppercase tracking-widest">
          Sponsor Dashboard
        </p>
        <p className="text-[10px] font-semibold text-white">Your Protégés</p>
      </div>

      {/* Protégé cards */}
      {[
        { name: 'J.T.', step: 'Step 3', badge: 'Active', color: 'rgba(79,137,132,0.3)', badgeText: '#4abbb5' },
        { name: 'M.R.', step: 'Step 7', badge: 'Review Pending', color: 'rgba(212,168,67,0.2)', badgeText: '#D4A843' },
      ].map((p) => (
        <div
          key={p.name}
          className="rounded-xl p-2"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                style={{ background: p.color }}
              >
                {p.name}
              </div>
              <div>
                <p className="text-[8px] font-semibold text-white">{p.name}</p>
                <p className="text-[6px] text-white/30">{p.step}</p>
              </div>
            </div>
            <span
              className="text-[6px] font-semibold px-1.5 py-0.5 rounded-full"
              style={{ background: `${p.color}`, color: p.badgeText }}
            >
              {p.badge}
            </span>
          </div>
        </div>
      ))}

      {/* Homework section */}
      <div
        className="rounded-xl p-2"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <p className="text-[7px] font-semibold text-white mb-1">Homework Due</p>
        <div className="flex items-center justify-between">
          <p className="text-[6.5px] text-white/40">Step 3 Prayer — J.T.</p>
          <span className="text-[5.5px] font-semibold px-1 py-0.5 rounded-full" style={{ background: 'rgba(193,68,75,0.15)', color: '#C1444B' }}>
            Due today
          </span>
        </div>
      </div>

      {/* Quick action */}
      <div
        className="rounded-xl py-2 text-center"
        style={{ background: 'rgba(79,137,132,0.18)', border: '1px solid rgba(79,137,132,0.3)' }}
      >
        <p className="text-[7.5px] font-semibold text-teal-300">+ Assign Homework</p>
      </div>
    </div>
  );
}

// ── Phone frame wrapper ───────────────────────────────────────────────────────

interface PhoneProps {
  label: string;
  description: string;
  accent?: string;
  delay?: number;
  rotate?: number;
  children: React.ReactNode;
}

function PhoneFrame({ label, description, accent = '#4f8984', delay = 0, rotate = 0, children }: PhoneProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] as const }}
      className="flex flex-col items-center gap-4"
    >
      {/* Phone */}
      <div
        className="relative rounded-[40px] overflow-hidden"
        style={{
          width: '200px',
          height: '410px',
          background: '#0b101c',
          border: '1.5px solid rgba(255,255,255,0.11)',
          boxShadow:
            '0 40px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.07)',
          transform: `rotate(${rotate}deg)`,
        }}
      >
        {/* Dynamic island */}
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-16 h-5 rounded-full bg-black z-20" />

        {/* Status bar */}
        <div className="flex justify-between items-center px-4 pt-9 pb-1.5 text-[7px] text-white/25 font-medium">
          <span>9:41</span>
          <span>●●●</span>
        </div>

        {/* Screen content */}
        {children}

        {/* Bottom indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full bg-white/15" />
      </div>

      {/* Label */}
      <div className="text-center">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-1.5 text-xs font-semibold"
          style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}44` }}
        >
          {label}
        </div>
        <p className="text-sm text-slate-400 max-w-[180px] leading-snug">{description}</p>
      </div>
    </motion.div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

export function AppShowcaseSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden bg-[#0D1321]">
      {/* Mountains backdrop — very subtle, bottom-anchored */}
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{ height: '45%' }}
        aria-hidden="true"
      >
        {/* Fade from page bg into the mountain silhouette */}
        <div
          className="absolute inset-x-0 top-0 z-10"
          style={{
            height: '65%',
            background: 'linear-gradient(to bottom, #0D1321 0%, transparent 100%)',
          }}
        />
        <img
          src="/assets/mountains.svg"
          alt=""
          draggable="false"
          className="absolute bottom-0 left-0 w-full opacity-15"
          style={{ height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 text-xs font-medium text-teal-300"
            style={{ background: 'rgba(79,137,132,0.1)', border: '1px solid rgba(79,137,132,0.25)' }}
          >
            Built for recovery
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Everything you need,{' '}
            <span className="text-teal-400" style={{ textShadow: '0 0 40px rgba(79,160,155,0.35)' }}>
              in one place.
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            From your morning intention to your nightly inventory — Daily Reprieve walks beside you
            through every part of the program.
          </p>
        </motion.div>

        {/* Three phones */}
        <div className="flex flex-col sm:flex-row items-end justify-center gap-8 sm:gap-6 lg:gap-10">
          {/* Dashboard — center, tallest */}
          <div className="sm:order-2 sm:mb-0">
            <PhoneFrame
              label="Dashboard"
              description="Your sobriety, step work, and daily check-ins — at a glance."
              delay={0.1}
              rotate={0}
            >
              <DashboardScreen />
            </PhoneFrame>
          </div>

          {/* Morning — left, slightly lower */}
          <div className="sm:order-1 sm:mb-[-24px]">
            <PhoneFrame
              label="Morning Check-In"
              description="Start each day with gratitude and intention."
              accent="#64c8c3"
              delay={0.2}
              rotate={-3}
            >
              <MorningCheckinScreen />
            </PhoneFrame>
          </div>

          {/* Nightly — right, slightly lower */}
          <div className="sm:order-3 sm:mb-[-24px]">
            <PhoneFrame
              label="Nightly Review"
              description="End each day with honest reflection and AI insights."
              accent="#6b9ac4"
              delay={0.3}
              rotate={3}
            >
              <NightlyReviewScreen />
            </PhoneFrame>
          </div>
        </div>

        {/* Second row — Step Work + Sponsor */}
        <div className="flex flex-col sm:flex-row items-start justify-center gap-8 sm:gap-10 mt-8">
          <PhoneFrame
            label="Step Work"
            description="All 12 steps with guided reflections. Sponsor review built in."
            accent="#a67c5b"
            delay={0.35}
            rotate={-2}
          >
            <StepWorkScreen />
          </PhoneFrame>
          <PhoneFrame
            label="Sponsor Tools"
            description="Assign homework, review step work, and track your protégés."
            accent="#778DA9"
            delay={0.45}
            rotate={2}
          >
            <SponsorScreen />
          </PhoneFrame>
        </div>

        {/* Bottom feature list */}
        <motion.div
          className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {[
            '✓ All 12 steps',
            '✓ 24 fellowships',
            '✓ Sponsor tools',
            '✓ AI nightly analysis',
            '✓ Push reminders',
            '✓ 100% private',
          ].map((item) => (
            <span key={item} className="text-sm text-slate-400 font-medium">
              {item}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
